import type { OrderRow } from "./orders.functions";
import type { AdminOrderLine } from "./order-lines.functions";
import type { PrintBundle } from "./orders-batch-print";
import { MEAL_SLOTS } from "./production.schemas";
import { slotsFromCombination } from "@/lib/mp2/delivery-charge";
import { tiffinDates, tiffinSlots, tiffinTitle } from "./order-type";

export type DispatchItem = {
  id: string;
  title: string;
  quantity: number;
  calories: number | null;
  protein: number | null;
};
export type DispatchUnit = {
  id: string;
  order: OrderRow;
  type: "a_la_carte" | "meal_plan" | "tiffin";
  date: string | null;
  slot: string;
  time: string;
  startDate: string | null;
  items: DispatchItem[];
  dispatchStatus: string;
  kitchenStatus: string;
  driver: string;
  vehicle: string;
  driverId: string | null;
  vehicleId: string | null;
  workflowOrderId: string | null;
  provenance: "verified" | "excluded" | "unknown";
  review: string | null;
};
export type DispatchBoardData = { units: DispatchUnit[]; reviews: DispatchUnit[]; excluded: DispatchUnit[]; selectedDateReviews: number };
export type DispatchFilters = { type: string; slot: string; area: string; kitchen: string; dispatch: string };
export type SavedRecord = Record<string, unknown>;

export const text = (v: unknown): string => typeof v === "string" ? v : "";
export const object = (v: unknown): SavedRecord => v && typeof v === "object" && !Array.isArray(v) ? v as SavedRecord : {};
export const records = (v: unknown): SavedRecord[] => Array.isArray(v) ? v.map(object) : [];
const number = (v: unknown): number | null => v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v);
const stopped = new Set(["cancelled", "failed", "refunded"]);
const eligibleLedger = new Set(["scheduled", "delivered"]);
export type ProvenanceEvidence = { audit?: SavedRecord[]; payments?: SavedRecord[]; verificationOrderIds?: string[] };
export function dispatchProvenance(order: OrderRow, evidence: ProvenanceEvidence = {}) {
  const raw = order as unknown as SavedRecord;
  const audit = (evidence.audit ?? []).filter(a => a.order_id === order.id);
  if (raw.is_demo === true || evidence.verificationOrderIds?.includes(order.id))
    return { status: "excluded" as const, reason: raw.is_demo === true ? "Demo record" : "Documented verification-cart lineage" };
  if (audit.some(a => a.event === "order_created" && a.outcome === "ok" && ["test", "uat"].includes(text(a.checkout_mode))))
    return { status: "excluded" as const, reason: "Creation audit confirms test / UAT checkout" };
  // Historical checkout code hard-coded payment_environment=test: it is not proof of synthetic origin.
  const liveCreation = audit.some(a => a.event === "order_created" && a.outcome === "ok"
    && a.checkout_mode === "live" && a.payment_environment === "live" && a.actor_is_admin === false);
  const liveCapture = raw.payment_environment === "live" && raw.payment_status === "captured"
    && (evidence.payments ?? []).some(p => p.order_id === order.id && p.is_demo === false && p.status === "captured"
      && p.signature_verified_at && p.captured_at && p.razorpay_payment_id && p.razorpay_payment_id === raw.razorpay_payment_id
      && p.razorpay_order_id === raw.razorpay_order_id);
  if (liveCreation || liveCapture) return { status: "verified" as const, reason: "Corroborated live checkout / verified provider capture" };
  return { status: "unknown" as const, reason: raw.payment_environment === "test"
    ? "Test environment marker without reliable creation history; customer intent unverified"
    : "No corroborating live creation audit or verified live payment; customer intent unverified" };
}
export function validDay(v: unknown): v is string {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

/** Pure read projection. Ledger dates supersede old materialised subscription dates. */
export function projectDispatch(input: {
  day: string; orders: OrderRow[]; items: SavedRecord[]; lines: SavedRecord[];
  ledger: SavedRecord[]; subscriptions: SavedRecord[]; dispatch: SavedRecord[]; dishes: SavedRecord[];
  combinations?: SavedRecord[]; menus?: SavedRecord[];
  /** Saved Tiffin subscriptions whose schedule includes the selected day. */
  tiffin?: SavedRecord[];
  provenance?: ProvenanceEvidence;
}): DispatchBoardData {
  const orders = new Map(input.orders.map(o => [o.id, o]));
  const subs = new Map(input.subscriptions.map(s => [text(s.id), s]));
  const dishes = new Map(input.dishes.map(d => [text(d.id), d]));
  const dispatch = new Map(input.dispatch.map(d => [text(d.order_id), d]));
  const units: DispatchUnit[] = [], reviews: DispatchUnit[] = [], excluded: DispatchUnit[] = [];
  const consumed = new Set<string>();
  const add = (order: OrderRow, type: DispatchUnit["type"], id: string, date: string | null,
    slot: string, sourceItems: SavedRecord[], sub?: SavedRecord, led?: SavedRecord) => {
    // A multi-day Tiffin shares one parent order, so per-day assignment is not safe there.
    const workflowId = type === "a_la_carte" ? order.id : type === "tiffin" ? (sub && Number(sub.service_days) === 1 ? order.id : null)
      : text(led?.fulfilled_order_id) || null;
    const d = workflowId ? dispatch.get(workflowId) : undefined;
    const workflowOrder = workflowId ? orders.get(workflowId) : undefined;
    const items = sourceItems.map((i): DispatchItem => {
      const dish = dishes.get(text(i.dish_id));
      return { id: text(i.id), title: text(i.dish_name_snapshot) || text(i.title_snapshot),
        quantity: number(i.quantity) ?? 0,
        calories: number(dish?.calories_kcal) ?? number(i.calories_kcal_snapshot),
        protein: number(dish?.protein_g) ?? number(i.protein_g_snapshot) };
    });
    const scheduleReview = !date ? "Delivery date is missing or conflicting" : !order.customer_name || !order.phone || !(order.address_line_1 || order.delivery_address)
      ? "Customer or delivery address needs confirmation" : !MEAL_SLOTS.includes(slot as typeof MEAL_SLOTS[number])
      ? "Meal slot is missing" : items.length === 0 || items.some(i => !i.title || i.quantity <= 0)
        ? "Scheduled meal details need confirmation" : null;
    const kitchen = workflowOrder?.status ?? (type === "meal_plan" ? "scheduled" : order.status);
    const related = [order, ...(sub ? [orders.get(text(sub.originating_order_id))] : [])].filter((o): o is OrderRow => Boolean(o));
    const checks = related.map(o => dispatchProvenance(o, input.provenance));
    const provenance = checks.find(p => p.status === "excluded") ?? checks.find(p => p.status === "unknown") ?? checks[0];
    const sourceDemo = sourceItems.some(i => i.is_demo === true) || sub?.is_demo === true || led?.is_demo === true;
    const status = sourceDemo ? "excluded" : provenance?.status ?? "unknown";
    const review = [status !== "verified" ? sourceDemo ? "Demo delivery source" : provenance?.reason : null, scheduleReview].filter(Boolean).join("; ") || null;
    const unit: DispatchUnit = {
      id, order, type, date, slot, time: type === "tiffin"
        ? text(object(sub?.price_snapshot)[`${slot}_window`])
        : order.meal_category === slot ? order.preferred_delivery_time || "" : "",
      startDate: text(sub?.start_date) || null, items,
      dispatchStatus: text(led?.status) === "delivered" ? "delivered" : text(d?.delivery_state) || "pending",
      kitchenStatus: kitchen, driver: text(d?.driver_name), vehicle: text(d?.vehicle_number),
      driverId: text(d?.driver_id) || null, vehicleId: text(d?.vehicle_id) || null,
      workflowOrderId: status === "verified" && !review ? workflowId : null, provenance: status, review,
    };
    if (date && date !== input.day) return;
    if (status === "excluded") excluded.push(unit);
    else if (review) reviews.push(unit);
    else units.push(unit);
  };

  for (const l of input.ledger) {
    const sub = subs.get(text(l.subscription_id));
    if (!sub || !eligibleLedger.has(text(l.status))) continue;
    if (text(l.status) !== "delivered" && text(sub.status) !== "active") continue;
    const origin = orders.get(text(sub.originating_order_id));
    if (!origin || stopped.has(origin.status)) continue;
    const fulfilled = orders.get(text(l.fulfilled_order_id));
    if (fulfilled && stopped.has(fulfilled.status)) continue;
    const date = validDay(l.delivery_date) ? l.delivery_date : null;
    const ids = new Set([origin.id, text(l.fulfilled_order_id)].filter(Boolean));
    const siblings = input.subscriptions.filter(s => text(s.originating_order_id) === origin.id);
    let items = input.items.filter(i => ids.has(text(i.order_id)) && text(i.delivery_date) === date
      && (text(i.source_ref) === text(sub.id) || text(i.order_id) === text(l.fulfilled_order_id)
        || (!text(i.source_ref) && siblings.length === 1)));
    if (fulfilled && items.some(i => text(i.order_id) === fulfilled.id)) items = items.filter(i => text(i.order_id) === fulfilled.id);
    items.forEach(i => consumed.add(text(i.id)));
    const bySlot = new Map<string, SavedRecord[]>();
    for (const i of items) { const slot = text(i.meal_slot); if (slot) bySlot.set(slot, [...(bySlot.get(slot) ?? []), i]); }
    const combination = input.combinations?.find(c => text(c.id) === text(sub.combination_id));
    let slots: string[] = [];
    if (combination) { try { slots = slotsFromCombination(text(combination.code)); } catch { /* Unknown combinations are flagged, not guessed. */ } }
    if (date && siblings.length === 1) for (const slot of slots) {
      if (bySlot.has(slot)) continue;
      // Reuse exact-date published weekly menu rules; never another week's menu or an alternate guess.
      const menu = input.menus?.find(m => text(m.plan_id) === text(sub.plan_id)
        && text(m.food_preference_code) === text(sub.preference_code) && text(m.week_start_date) === monday(date));
      const day = records(menu?.menu_cycle_days).find(d => Number(d.day_of_week) === (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7);
      const choices = records(day?.menu_cycle_day_dishes).filter(d => text(d.meal_slot) === slot && d.is_primary === true);
      const choice = choices.length === 1 ? choices[0] : undefined;
      const dish = object(choice?.mp2_dishes);
      bySlot.set(slot, text(dish.name) ? [{ id: `menu:${text(l.id)}:${slot}`, dish_id: text(dish.id), dish_name_snapshot: text(dish.name),
        quantity: origin.quantity, calories_kcal_snapshot: dish.calories_kcal, protein_g_snapshot: dish.protein_g }] : []);
    }
    if (bySlot.size === 0) bySlot.set("", []);
    for (const [slot, selected] of bySlot) add(fulfilled ?? origin, "meal_plan", `ledger:${text(l.id)}:${slot}`, date, slot, selected, sub, l);
  }

  // Tiffin: saved subscription schedule is the delivery source. Only paid (active/completed) subscriptions dispatch.
  for (const t of input.tiffin ?? []) {
    const order = orders.get(text(t.order_id));
    if (!order || stopped.has(order.status) || !["active", "completed"].includes(text(t.status))) continue;
    if (!tiffinDates(t as never).includes(input.day)) continue;
    const addr = object(t.address_snapshot);
    const withAddress = order.address_line_1 || order.delivery_address ? order
      : { ...order, delivery_address: text(addr.address), neighbourhood: order.neighbourhood || text(addr.area) };
    const slots = tiffinSlots(t as never);
    const item = { id: `tiffin:${text(t.id)}`, title_snapshot: tiffinTitle(t as never), quantity: 1 };
    for (const slot of slots.length ? slots : [""]) add(withAddress, "tiffin", `tiffin:${text(t.id)}:${input.day}:${slot}`, input.day, slot, [item], t);
  }

  // Only orders without an authoritative subscription use their saved materialised dates.
  const subscribedOrderIds = new Set(input.subscriptions.flatMap(s => [text(s.originating_order_id)]));
  for (const l of input.ledger) if (text(l.fulfilled_order_id)) subscribedOrderIds.add(text(l.fulfilled_order_id));
  for (const order of input.orders) {
    if (stopped.has(order.status) || subscribedOrderIds.has(order.id)) continue;
    const raw = order as unknown as SavedRecord;
    if (text(raw.order_type) === "tiffin") continue;
    const orderItems = input.items.filter(i => text(i.order_id) === order.id && !consumed.has(text(i.id)));
    const orderLines = input.lines.filter(i => text(i.order_id) === order.id);
    const fallbackLines = orderLines.filter(l => text(l.kind) !== "meal_plan" && !orderItems.some(i => text(i.order_line_id) === text(l.id)
      || ((!text(i.order_line_id)) && (text(i.line_kind) === text(l.kind))
        && (text(i.dish_id) ? text(i.dish_id) === text(l.dish_id) : text(i.dish_name_snapshot) === text(l.title_snapshot))
        && text(i.meal_slot) === text(l.meal_slot_snapshot))));
    const groups = new Map<string, { type: DispatchUnit["type"]; date: string | null; slot: string; items: SavedRecord[] }>();
    for (const i of [...orderItems, ...fallbackLines]) {
      const type = text(i.line_kind) === "meal_plan" || text(i.source) === "meal_plan" || text(raw.order_type) === "meal_plan" ? "meal_plan" : "a_la_carte";
      const saved = text(i.delivery_date) || text(i.delivery_date_snapshot);
      const date = saved ? validDay(saved) ? saved : null : type === "a_la_carte" && validDay(order.preferred_delivery_date) ? order.preferred_delivery_date : null;
      const slot = text(i.meal_slot) || text(i.meal_slot_snapshot) || (type === "a_la_carte" ? order.meal_category : "");
      const key = `${type}:${date}:${slot}`;
      const group = groups.get(key) ?? { type, date, slot, items: [] };
      group.items.push(i); groups.set(key, group);
    }
    if (groups.size === 0) {
      const type = text(raw.order_type) === "meal_plan" || orderLines.some(l => text(l.kind) === "meal_plan") ? "meal_plan" : "a_la_carte";
      add(order, type, `order:${order.id}:review`, type === "a_la_carte" && validDay(order.preferred_delivery_date) ? order.preferred_delivery_date : null, order.meal_category, []);
    } else for (const [key, g] of groups) add(order, g.type, `order:${order.id}:${key}`, g.date, g.slot, g.items);
  }
  const unique = (rows: DispatchUnit[]) => [...new Map(rows.map(r => [r.id, r])).values()];
  const result = unique(units);
  // A parent-order assignment cannot safely update just one recurring/slot delivery.
  const workflowCounts = new Map<string, number>();
  for (const r of result) if (r.workflowOrderId) workflowCounts.set(r.workflowOrderId, (workflowCounts.get(r.workflowOrderId) ?? 0) + 1);
  for (const r of result) if (r.workflowOrderId && (workflowCounts.get(r.workflowOrderId) !== 1
    || input.items.some(i => text(i.order_id) === r.workflowOrderId && validDay(i.delivery_date) && i.delivery_date !== r.date))) r.workflowOrderId = null;
  const uniqueReviews = unique(reviews);
  return { units: result, reviews: uniqueReviews, excluded: unique(excluded), selectedDateReviews: uniqueReviews.filter(r => r.date === input.day).length };
}

export function monday(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

export function filterDispatch(rows: DispatchUnit[], f: DispatchFilters): DispatchUnit[] {
  return rows.filter(r => (f.type === "all" || r.type === f.type) && (f.slot === "all" || r.slot === f.slot)
    && (f.area === "all" || r.order.neighbourhood === f.area) && (f.kitchen === "all" || r.kitchenStatus === f.kitchen)
    && (f.dispatch === "all" || r.dispatchStatus === f.dispatch));
}
export function overdue(r: DispatchUnit, now: Date): boolean {
  if (!r.date || !r.time || ["delivered", "returned", "failed"].includes(r.dispatchStatus)) return false;
  const time = /^\d{2}:\d{2}/.exec(r.time)?.[0];
  return Boolean(time && new Date(`${r.date}T${time}:00+05:30`).getTime() < now.getTime());
}
export function sortDispatch(rows: DispatchUnit[], now: Date): DispatchUnit[] {
  return [...rows].sort((a, b) => Number(b.dispatchStatus !== "delivered") - Number(a.dispatchStatus !== "delivered")
    || Number(overdue(b, now)) - Number(overdue(a, now)) || (a.time || "99:99").localeCompare(b.time || "99:99")
    || MEAL_SLOTS.indexOf(a.slot as typeof MEAL_SLOTS[number]) - MEAL_SLOTS.indexOf(b.slot as typeof MEAL_SLOTS[number])
    || a.order.order_ref.localeCompare(b.order.order_ref));
}
export function dispatchSummary(rows: DispatchUnit[], now: Date) {
  return { total: rows.length, breakfast: rows.filter(r => r.slot === "breakfast").length,
    lunch: rows.filter(r => r.slot === "lunch").length, snacks: rows.filter(r => r.slot === "snacks").length,
    dinner: rows.filter(r => r.slot === "dinner").length,
    pending: rows.filter(r => ["scheduled", "new", "confirmed", "accepted", "preparing"].includes(r.kitchenStatus) && r.dispatchStatus === "pending").length,
    ready: rows.filter(r => r.kitchenStatus === "ready" && r.dispatchStatus === "pending").length,
    out: rows.filter(r => r.dispatchStatus === "out_for_delivery").length,
    overdue: rows.filter(r => overdue(r, now)).length };
}

/** Virtual print IDs isolate each selected delivery; never persisted. Approved renderer is unchanged. */
export function dispatchPrintBundle(rows: DispatchUnit[]): PrintBundle {
  if (rows.length === 0 || rows.some(r => r.provenance !== "verified" || r.review || !r.date || r.items.length === 0)) throw new Error("Some deliveries need review before printing.");
  const lines: Record<string, AdminOrderLine[]> = {}, dispatch: PrintBundle["dispatch"] = {};
  const orders = rows.map(r => {
    lines[r.id] = r.items.map((i, n) => ({ id: i.id, line_no: n + 1, kind: "dish", title_snapshot: i.title,
      meal_slot_snapshot: r.slot, delivery_date_snapshot: r.date, quantity: i.quantity, unit_price_paise: null,
      line_total_paise: null, detail_snapshot: null, calories_kcal: i.calories, protein_g: i.protein }));
    dispatch[r.id] = { driver_name: r.driver, vehicle_number: r.vehicle };
    return { ...r.order, id: r.id, preferred_delivery_date: r.date ?? "", preferred_delivery_time: r.time,
      meal_category: r.slot as OrderRow["meal_category"], quantity: Math.max(...r.items.map(i => i.quantity)) };
  });
  return { orders, lines, dispatch };
}